/* app/report-drawer.jsx - a run report folded to one line, opened on demand.

   The post-run reports on the banks and the planners earn their length: every
   sentence in them answers a question someone eventually asks (which vocabulary
   ran, what was skipped and why, what is still waiting). But a report that
   tall, sitting above the table it reports on, costs the screen it lands on
   more than it gives back, and the owner's ask was exact: a line with the
   title, clickable, opening a drawer.

   So this is a fold, not a summary. The strip carries ONE line: the outcome
   with its numbers, a chevron saying there is more, and the report's own
   Dismiss. The full report moves into a right-side drawer, word for word,
   because shortening it would re-fight the battle those sentences already won
   (see the ClassifyRunReport comments in research-keywords.jsx and
   research-topics.jsx on why a toast was not enough).

   The drawer is the design system's own (window.HuddleDesignSystem_775113
   .Drawer, the one Bulk Meta's row editor uses): fixed scrim, 480px panel,
   full height, scrolling body, close on the X or the scrim. Escape is added
   here because the DS drawer does not listen for it. The strip stays mounted
   while the drawer is open, so dismissing is possible from either place and
   means the same thing: the host clears its report state and both go.

   Attached to window like every other module; screens reach it at render time
   as window.MlReportDrawer, so import order against them does not matter. */
(function () {
  const { useState, useEffect } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }

  // The same three tones the report cards used, so a folded report keeps the
  // colour its full-size self had: a failed run must not fold into a calm line.
  const TONE_ICON = { problem: "alert-triangle", todo: "circle", opportunity: "check-circle" };

  /**
   * One line that is the report's title, and a drawer that is the report.
   *
   * `title` states the OUTCOME with its numbers; the caller derives it from the
   * same data the report body reads, never from anywhere else, so the line and
   * the drawer can never disagree. `tone` is problem | todo | opportunity.
   * `onDismiss` is the report's existing dismiss semantics (clear the host's
   * report state); when a report never had a dismiss control, none is rendered
   * here either. `children` is the report body, unchanged.
   *
   * The whole line opens the drawer EXCEPT the Dismiss button, which stops the
   * click: throwing a report away must never also open it.
   */
  function ReportStrip({ title, tone = "todo", onDismiss, children }) {
    const [open, setOpen] = useState(false);
    const DS = window.HuddleDesignSystem_775113;
    const Drawer = DS && DS.Drawer;

    // Escape closes the drawer. Bound only while it is open, so a strip at
    // rest costs the page nothing and swallows nobody else's Escape.
    useEffect(() => {
      if (!open) return;
      const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    return (
      <>
        <div
          className={`ds-nudge ds-nudge--${tone} ml-rdrawer-strip`}
          role="button"
          tabIndex={0}
          aria-expanded={open}
          title="Open the full report"
          onClick={() => setOpen(true)}
          /* Keyboard path for the row itself only: an Enter pressed on the
             Dismiss button inside is that button's business. */
          onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen(true); } }}
        >
          <span className="ds-card__icon"><I name={TONE_ICON[tone] || "circle"} size={15} /></span>
          <span className="ds-nudge__text"><b>{title}</b></span>
          <span className="ds-card__icon ml-rdrawer-chev"><I name="chevron-right" size={15} /></span>
          {onDismiss && (
            <button className="bi-btn bi-btn--sm" onClick={(e) => { e.stopPropagation(); onDismiss(); }}>Dismiss</button>
          )}
        </div>
        {open && (
          Drawer
            ? <Drawer title={title} subtitle="The full report" onClose={() => setOpen(false)}>{children}</Drawer>
            /* The DS bundle failing to load is index.html's problem, but a
               click that does nothing must not be this component's answer to
               it: the report renders inline instead, which is exactly what it
               did before it was folded. */
            : <div className="ds-strip">{children}</div>
        )}
      </>
    );
  }

  /**
   * The same fold for a FINDING rather than a report: a line carrying the
   * finding and, optionally, the one control that acts on it, with everything
   * that explains it in the drawer.
   *
   * Separate from ReportStrip because the two are not the same object. A
   * report is finished and its only line-level verb is "throw it away", so
   * its control is Dismiss. A finding is live and its line-level verb is the
   * work itself, so `action` sits on the line and keeps its click: making a
   * user open a drawer to reach "Select all 60" would add a click to the
   * thing they came for.
   */
  function DetailLine({ title, tone = "todo", subtitle, action, children }) {
    const [open, setOpen] = useState(false);
    const DS = window.HuddleDesignSystem_775113;
    const Drawer = DS && DS.Drawer;
    useEffect(() => {
      if (!open) return;
      const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, [open]);
    return (
      <>
        <div className={`ds-line ds-line--${tone}`} role="button" tabIndex={0}
             aria-expanded={open} title="Open the details"
             onClick={() => setOpen(true)}
             onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen(true); } }}>
          <span className="ds-card__icon"><I name={TONE_ICON[tone] || "circle"} size={15} /></span>
          <span className="ds-line__title">{title}</span>
          {action && <span onClick={(e) => e.stopPropagation()}>{action}</span>}
          <span className="ds-card__icon ml-rdrawer-chev"><I name="chevron-right" size={15} /></span>
        </div>
        {open && (Drawer
          ? <Drawer title={title} subtitle={subtitle || "The details"} onClose={() => setOpen(false)}>{children}</Drawer>
          : <div className="ds-strip">{children}</div>)}
      </>
    );
  }

  window.MlReportDrawer = { ReportStrip, DetailLine };
})();
